export const navLinks = [
  { label: "Opportunity", href: "#opportunity" },
  { label: "Solutions", href: "#solutions" },
  { label: "Process", href: "#process" },
  { label: "Numbers", href: "#numbers" },
  { label: "Product", href: "#products" },
  { label: "About", href: "#about" },
];

export const heroStats = [
  { value: 50, unit: "t/day", label: "Green iron (HBI) output", decimals: 0 },
  { value: 16500, unit: "t", label: "Per module, per year", decimals: 0 },
  { value: 2.85, unit: "t/day", label: "Green hydrogen produced", decimals: 2 },
];

export const whyNow = [
  {
    tag: "01",
    icon: "ri-price-tag-3-line",
    title: "Carbon is becoming a cost",
    text: "The EU Carbon Border Adjustment Mechanism entered its definitive phase in January 2026, pricing the carbon embedded in imported steel.",
  },
  {
    tag: "02",
    icon: "ri-government-line",
    title: "India has set the direction",
    text: "The National Green Hydrogen Mission and the Ministry of Steel green steel taxonomy create a clear policy path for low-carbon steel.",
  },
  {
    tag: "03",
    icon: "ri-shopping-bag-3-line",
    title: "Buyers are asking",
    text: "Automotive, infrastructure and export customers increasingly want verified low-carbon steel in their supply chains.",
  },
];

export const solutions = [
  {
    icon: "ri-flask-line",
    title: "Green hydrogen",
    description:
      "Water plus renewable power becomes hydrogen. Raw water is purified to electrolyser grade and split into hydrogen and oxygen using solar and wind electricity.",
    points: ["Purified to electrolyser grade", "Solar + wind powered", "Compressed, dried and stored"],
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20modern%20industrial%20hydrogen%20direct%20reduction%20shaft%20furnace%20silhouette%20with%20soft%20green%20lighting%20and%20clean%20minimal%20geometric%20background%2C%20editorial%20industrial%20photography%2C%20cool%20tones%2C%20high%20detail&width=900&height=700&seq=gs-sol-hydrogen&orientation=landscape",
  },
  {
    icon: "ri-fire-line",
    title: "Hydrogen direct reduction",
    description:
      "DR-grade iron ore pellets meet hydrogen in the shaft furnace. Hydrogen strips the oxygen, leaving solid sponge iron and water vapour — then it is pressed into HBI.",
    points: ["Iron ore pellets + H₂ → DRI", "Water vapour, not CO₂", "Off-gas hydrogen recovered"],
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20electric%20arc%20furnace%20melting%20chamber%20with%20warm%20amber%20glow%20and%20clean%20minimal%20green%20steel%20foundry%20interior%2C%20editorial%20industrial%20photography%2C%20high%20detail%2C%20harmonious%20composition&width=900&height=700&seq=gs-sol-eaf&orientation=landscape",
  },
  {
    icon: "ri-flashlight-line",
    title: "Electric arc melting",
    description:
      "Phase 2 melts DRI, with scrap if required, under graphite electrodes at about 1,600–1,700°C. Lime, fluxes and alloys set the chemistry; impurities leave as slag.",
    points: ["DRI + electricity → molten steel", "1,600–1,700°C graphite electrodes", "Precise alloy control"],
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20circular%20recycled%20steel%20scrap%20loop%20rendered%20as%20clean%20minimal%20industrial%20art%20with%20green%20and%20steel%20gray%20tones%2C%20editorial%20photography%2C%20high%20detail%2C%20harmonious%20composition&width=900&height=700&seq=gs-sol-scrap&orientation=landscape",
  },
  {
    icon: "ri-sun-line",
    title: "Renewable PPA power",
    description:
      "We buy power from a solar–wind hybrid park under a long-term purchase agreement, delivered through Andhra Pradesh Green Energy Open Access — no power station to build.",
    points: ["Third-party solar–wind PPA", "Grid banking up to 30%", "Fixed ₹/kWh for 20–25 years"],
    image:
      "https://readdy.ai/api/search-image?query=Abstract%20renewable%20energy%20solar%20panels%20and%20wind%20turbines%20beside%20a%20clean%20modern%20steel%20plant%2C%20minimal%20green%20tones%2C%20editorial%20industrial%20photography%2C%20high%20detail%2C%20harmonious%20composition&width=900&height=700&seq=gs-sol-energy&orientation=landscape",
  },
];

export const processSteps = [
  {
    step: "01",
    icon: "ri-flask-line",
    title: "Green hydrogen",
    description:
      "Water + renewable power → H₂. Raw water is purified (filtration, RO, polishing) to electrolyser grade, then split into hydrogen and oxygen with solar and wind electricity.",
  },
  {
    step: "02",
    icon: "ri-fire-line",
    title: "Hydrogen direct reduction",
    description:
      "Iron ore pellets + H₂ → DRI. Hydrogen strips oxygen from DR-grade pellets, producing sponge iron and water vapour. Off-gas is cooled and unused hydrogen recycled.",
  },
  {
    step: "03",
    icon: "ri-flashlight-line",
    title: "Electric arc furnace",
    description:
      "Phase 2 · DRI + electricity → molten steel. DRI, with scrap if required, is melted by graphite electrodes at about 1,600–1,700°C. Impurities leave as slag.",
  },
  {
    step: "04",
    icon: "ri-layout-grid-line",
    title: "Refining, casting & rolling",
    description:
      "Phase 2 · Ladle refining (LF / VD) fine-tunes carbon, alloys and cleanliness. Continuous casting and rolling turn it into TMT bars, wire rods and plates.",
  },
];

export const calculatorRules = [
  {
    tag: "01",
    title: "Ore loses about 30% of its weight",
    text: "Pellets are 67% iron; the rest is mostly oxygen. Taking it out leaves 0.72 t of iron per tonne of pellets, so 50 t of HBI needs about 71 t of pellets.",
  },
  {
    tag: "02",
    title: "1 kg of hydrogen for every 8 kg of oxygen",
    text: "Water is H₂O. Removing 0.38 t of oxygen per tonne takes 48 kg of hydrogen by chemistry; we plan 57 kg to cover losses.",
  },
  {
    tag: "03",
    title: "52 kWh for each kilo of hydrogen",
    text: "A modern electrolyser uses 50–55 kWh per kg. Heating the gas, compressors and the press add about 0.5 MWh per tonne.",
  },
];

export const keyNumbers = [
  { value: "16,500 t", label: "Green iron per year" },
  { value: "23,400 t", label: "DR-grade pellets" },
  { value: "940 t", label: "Hydrogen consumed" },
  { value: "57 GWh", label: "Renewable power" },
  { value: "7,500 t", label: "Oxygen by-product" },
];

export const electricityTable = [
  { stage: "Hydrogen production (57 kg H₂ × 52 kWh)", value: "~2,960" },
  { stage: "Heating the hydrogen to ~900°C", value: "~350" },
  { stage: "Compressors, briquetting, utilities", value: "~150" },
  { stage: "Total per tonne of HBI", value: "~3,460" },
];

export const modelModules = [
  { modules: "1", iron: "50 t/day", year: "16,500 t", electrolyser: "~7 MW", power: "~23 MW", hydrogen: "2.85 t/day" },
  { modules: "2", iron: "100 t/day", year: "33,000 t", electrolyser: "~14 MW", power: "~46 MW", hydrogen: "5.7 t/day" },
  { modules: "4", iron: "200 t/day", year: "66,000 t", electrolyser: "~28 MW", power: "~92 MW", hydrogen: "11.4 t/day" },
];

export const pilotNumbers = [
  { icon: "ri-map-pin-2-line", value: "≈30 acres", label: "Site, room to grow to 200 t/day" },
  { icon: "ri-drop-line", value: "≈29 m³/day", label: "Water for hydrogen, ¾ recovered" },
  { icon: "ri-flashlight-line", value: "≈175 MWh/day", label: "Electricity, ≈3.5 MWh per tonne" },
  { icon: "ri-gas-station-line", value: "≈23 t/day", label: "Oxygen by-product for converters" },
];

export const products = [
  {
    name: "Green HBI",
    category: "Direct reduced iron",
    spec: "≈92% Fe · ≥93% metallisation · >5 g/cm³",
    grade: "ISO 10835",
    image:
      "https://readdy.ai/api/search-image?query=Neatly%20stacked%20dark%20grey%20hot%20briquetted%20iron%20HBI%20briquettes%20shaped%20like%20small%20soap%20bars%20on%20a%20minimal%20light%20studio%20background%2C%20product%20photography%2C%20soft%20shadow%2C%20high%20detail%2C%20consistent%20neutral%20backdrop&width=800&height=800&seq=gs-prod-hbi&orientation=squarish",
  },
  {
    name: "TMT Reinforcement Bars",
    category: "Construction",
    spec: "Phase 2 · 10 mm × 10 m bar ≈ 6.17 kg",
    grade: "IS 1786",
    image:
      "https://readdy.ai/api/search-image?query=Stack%20of%20clean%20ribbed%20green%20steel%20rebar%20bars%20on%20a%20minimal%20light%20studio%20background%2C%20product%20photography%2C%20soft%20shadow%2C%20high%20detail%2C%20consistent%20neutral%20backdrop&width=800&height=800&seq=gs-prod-rebar&orientation=squarish",
  },
  {
    name: "Wire Rods",
    category: "Industrial",
    spec: "Phase 2 · 5.5–16 mm coils",
    grade: "SAE 1006",
    image:
      "https://readdy.ai/api/search-image?query=Coiled%20fine%20green%20steel%20wire%20rod%20on%20a%20minimal%20light%20studio%20background%2C%20product%20photography%2C%20soft%20shadow%2C%20high%20detail%2C%20consistent%20neutral%20backdrop&width=800&height=800&seq=gs-prod-wire&orientation=squarish",
  },
  {
    name: "Heavy Plates",
    category: "Infrastructure",
    spec: "Phase 2 · 6–120 mm plate",
    grade: "S355J2",
    image:
      "https://readdy.ai/api/search-image?query=Stacked%20flat%20green%20steel%20heavy%20plates%20on%20a%20minimal%20light%20studio%20background%2C%20product%20photography%2C%20soft%20shadow%2C%20high%20detail%2C%20consistent%20neutral%20backdrop&width=800&height=800&seq=gs-prod-plate&orientation=squarish",
  },
  {
    name: "Hot-Rolled Coil",
    category: "Automotive",
    spec: "Phase 2 · 1.5–12 mm",
    grade: "S235JR",
    image:
      "https://readdy.ai/api/search-image?query=Neatly%20wound%20rolled%20green%20steel%20coil%20on%20a%20minimal%20light%20studio%20background%2C%20product%20photography%2C%20soft%20shadow%2C%20high%20detail%2C%20consistent%20neutral%20backdrop&width=800&height=800&seq=gs-prod-coil&orientation=squarish",
  },
  {
    name: "Structural H-Beam",
    category: "Infrastructure",
    spec: "Phase 2 · HEA–HEM series",
    grade: "EN 10025",
    image:
      "https://readdy.ai/api/search-image?query=Single%20clean%20steel%20H-beam%20structural%20profile%20on%20a%20minimal%20light%20studio%20background%2C%20product%20photography%2C%20soft%20shadow%2C%20high%20detail%2C%20consistent%20neutral%20backdrop&width=800&height=800&seq=gs-prod-beam&orientation=squarish",
  },
];

export const impactMetrics = [
  {
    icon: "ri-cloud-off-line",
    value: 96,
    suffix: "%",
    label: "Lower CO₂ than blast furnace",
    detail: "Reduction emits water vapour, not CO₂",
  },
  {
    icon: "ri-leaf-line",
    value: 100,
    suffix: "%",
    label: "Renewable-powered reduction",
    detail: "Solar–wind PPA with grid banking",
  },
  {
    icon: "ri-water-percent-line",
    value: 75,
    suffix: "%",
    label: "Process water recovered",
    detail: "≈29 m³/day, three-quarters recovered",
  },
  {
    icon: "ri-recycle-line",
    value: 23,
    suffix: " t/day",
    label: "Oxygen by-product",
    detail: "Captured and fed to steel converters",
  },
];

export const carbonComparison = [
  { label: "Conventional blast furnace", value: 2.4, width: "100%", tone: "bg-foreground-400" },
  { label: "India green steel limit", value: 2.2, width: "92%", tone: "bg-secondary-400" },
  { label: "DJT hydrogen route (reduction)", value: 0.1, width: "5%", tone: "bg-primary-500" },
];

export const aboutHighlights = [
  {
    icon: "ri-map-pin-2-line",
    title: "Rooted in Andhra Pradesh",
    text: "DJT EV Power Tech builds sustainable energy and green steel in India — an integrated plant where renewable power makes green hydrogen, and hydrogen turns iron ore into low-carbon iron.",
  },
  {
    icon: "ri-layout-grid-line",
    title: "Compact and modular",
    text: "The full chain, from hydrogen to green iron, sits on one 30-acre site, expandable module by module to 200 t/day as performance is proven.",
  },
  {
    icon: "ri-line-chart-line",
    title: "Proven abroad, ready for India",
    text: "Hydrogen steel is already running at HYBRIT and Stegra in Sweden. We bring the same fossil-free route to Indian steelmakers, starting at pilot scale.",
  },
];

export const faqs = [
  {
    q: "What makes this iron green?",
    a: "We replace coal and coke with green hydrogen. Hydrogen strips the oxygen from iron ore and leaves water vapour instead of carbon dioxide, so the reduction step releases almost no CO₂. Renewables power the whole chain.",
  },
  {
    q: "What is HBI and how is it used?",
    a: "Hot briquetted iron is direct-reduced iron pressed while hot into dense briquettes, each about the size of a bar of soap. Unlike loose sponge iron it does not rust through or catch fire, so it can be stored in the open and shipped by rail or road, then charged into blast, basic oxygen or electric arc furnaces.",
  },
  {
    q: "How is the plant powered?",
    a: "Through a long-term power purchase agreement with a solar–wind hybrid park, delivered under Andhra Pradesh Green Energy Open Access. Surplus daytime power is banked on the grid, a hydrogen buffer covers the night, and a firm renewable or battery-backed contract can be added as the plant grows.",
  },
  {
    q: "Can it scale beyond the pilot?",
    a: "Yes. The plant is built as standard 50 t/day modules. Each new module copies the last, so capacity grows step by step — one, two or four modules give 50, 100 or 200 t/day beside the steel plant that uses the iron.",
  },
  {
    q: "How does it fit India green steel policy?",
    a: "Under the Ministry of Steel notification of 12 December 2024, finished steel below 2.2 t CO₂e per tonne is green steel. Our near-zero HBI lowers the footprint of every tonne of steel it goes into, and the National Green Hydrogen Mission has set aside ₹455 crore for steel pilots.",
  },
];

export const footerLinks = {
  solutions: [
    { label: "Green Hydrogen", href: "#solutions" },
    { label: "Hydrogen DRI", href: "#solutions" },
    { label: "Electric Arc Melting", href: "#solutions" },
    { label: "Renewable PPA", href: "#solutions" },
  ],
  company: [
    { label: "About Us", href: "#about" },
    { label: "Our Process", href: "#process" },
    { label: "The Pilot", href: "#numbers" },
    { label: "Careers", href: "#contact" },
  ],
  resources: [
    { label: "Green Iron Calculator", href: "#numbers" },
    { label: "Policy Support", href: "#impact" },
    { label: "Green HBI", href: "#products" },
    { label: "Contact Sales", href: "#contact" },
  ],
};